import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2, Upload, X, CheckCircle, Sun, Moon, Users, ChevronDown, ChevronUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { jsPDF } from "jspdf";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import AgentSidebar from "../components/AgentSidebar";
import { useChatHistory } from "../hooks/useChatHistory";
import { API_CONFIG } from "../config/api";

const FeedbackAgent = ({ onNavigate }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [transcript, setTranscript] = useState(null);
  const [transcriptFileName, setTranscriptFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inputBarCollapsed, setInputBarCollapsed] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false); // Track if chat has started

  // Multi-step setup flow state
  const [setupStep, setSetupStep] = useState(1); // 1: JD, 2: Transcript, 3: Question
  const [setupCollapsed, setSetupCollapsed] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showSetupOptions, setShowSetupOptions] = useState(false); // Show setup choice after first response

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const MAX_PROMPTS_PER_CHAT = 10;

  const [currentSessionId, setCurrentSessionId] = useState(`session-${Date.now()}`);
  const { chatSessions, saveChatSession, loadChatSession, deleteChatSession } =
    useChatHistory(user?.email || user?.username || "anonymous", "feedback");

  const userPromptCount = messages.filter((msg) => msg.sender === "user").length;
  const isPromptLimitReached = userPromptCount >= MAX_PROMPTS_PER_CHAT;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const updateMessageText = (id, newText, isThinking = false, metrics = null) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, text: newText, isThinking, metrics } : m
      )
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const base64Content = content.split("base64,")[1];

      setTranscript({
        filename: file.name,
        content: base64Content,
      });
      setTranscriptFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const removeTranscript = () => {
    setTranscript(null);
    setTranscriptFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sendMessage = async () => {
    if (!transcript) {
      alert("Please upload transcript");
      return;
    }

    const text = inputValue || "Provide structured interview feedback";
    
    // Determine if this is a follow-up question
    const isFollowup = hasSubmitted && setupCollapsed;
    
    // Get the last bot response for context in follow-up questions
    const lastBotMessage = messages.filter(msg => msg.sender === 'bot' && !msg.isThinking).pop();
    const previousFeedback = isFollowup && lastBotMessage ? lastBotMessage.text : null;

    const userId = `u-${Date.now()}`;
    const userMessage = {
      id: userId,
      text: isFollowup ? text : `JD:\n${jobDescription}\n\nQuestion:\n${text}`,
      sender: "user",
    };
    pushMessage(userMessage);
    
    // Mark chat as started when first message is sent
    if (!hasStartedChat) {
      setHasStartedChat(true);
    }

    const thinkingId = `b-${Date.now()}`;
    pushMessage({
      id: thinkingId,
      text: "Analyzing feedback...",
      sender: "bot",
      isThinking: true,
    });

    setInputValue("");
    setIsLoading(true);

    try {
      // Use centralized API config - must be set in .env or Amplify
      const apiUrl = API_CONFIG.FEEDBACK_AGENT_URL;
      
      if (!apiUrl) {
        throw new Error("FEEDBACK_AGENT_URL is not configured. Please set REACT_APP_FEEDBACK_AGENT_URL in .env file.");
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_description: jobDescription,
          transcript,
          question: text,
          is_followup: isFollowup,  // Send follow-up flag to backend
          previous_feedback: previousFeedback,  // Send previous response for context
        }),
      });

      const data = await res.json();
      
      console.log('API Response:', data); // Debug log
      console.log('Response status:', res.status); // Debug status
      
      // Check if there's an error
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `API returned status ${res.status}`);
      }

      const responseText =
        data.response || data.content?.[0]?.text || data.text || "No response received";

      updateMessageText(thinkingId, responseText, false, data.metrics || null);

      const finalBotMessage = {
        id: thinkingId,
        text: responseText,
        sender: "bot",
        isThinking: false,
        metrics: data.metrics || null,
      };

      const completeMessages = [...messages, userMessage, finalBotMessage];
      await saveChatSession(
        currentSessionId,
        completeMessages,
        text.substring(0, 60)
      );

      // Collapse setup after first submission
      if (!hasSubmitted) {
        setHasSubmitted(true);
        setSetupCollapsed(true);
      }
      
      // Always show options after every bot response
      setShowSetupOptions(true);
    } catch (error) {
      updateMessageText(thinkingId, `⚠️ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameChat = async (sessionId, newTitle) => {
    try {
      const chatData = await loadChatSession(sessionId);
      if (!chatData) throw new Error('Failed to load chat data');
      if (!chatData.messages || !Array.isArray(chatData.messages)) {
        throw new Error('Invalid chat data');
      }
      await saveChatSession(sessionId, chatData.messages, newTitle);
    } catch (error) {
      console.error('Failed to rename chat:', error);
      alert(`Failed to rename chat: ${error.message}`);
    }
  };

  const handleDeleteChat = async (sessionId) => {
    try {
      await deleteChatSession(sessionId);
      if (sessionId === currentSessionId) {
        setMessages([]);
        setCurrentSessionId(`session-${Date.now()}`);
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  const handleShareChat = async (chat) => {
    try {
      console.log('=== SHARE CHAT DEBUG ===');
      console.log('Chat object:', chat);
      
      const chatData = await loadChatSession(chat.sessionId);
      
      console.log('Loaded chat data:', chatData);
      
      if (!chatData || !chatData.messages) {
        console.error('Invalid chat data:', chatData);
        alert('Failed to load chat data');
        return;
      }

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const checkNewPage = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to clean markdown formatting
      const cleanMarkdown = (text) => {
        if (!text) return '';
        
        return text
          // Remove markdown headers (###, ##, #)
          .replace(/^#{1,6}\s+/gm, '')
          // Remove bold/italic (**text**, *text*)
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1')
          // Remove inline code (`code`)
          .replace(/`([^`]+)`/g, '$1')
          // Remove blockquotes (>)
          .replace(/^>\s+/gm, '')
          // Remove horizontal rules (---, ***)
          .replace(/^[-*]{3,}$/gm, '')
          // Clean up extra whitespace
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      };

      // Helper function to detect if a line is a heading
      const isHeading = (line) => {
        return /^(Summary|Strengths|Improvements|Suspicion|Integrity|Role Fit|Recommendation|Scoring)/i.test(line.trim());
      };

      // Helper function to render text with bold headings
      const renderTextWithHeadings = (text, startY) => {
        const lines = text.split('\n');
        let currentY = startY;
        
        lines.forEach(line => {
          if (!line.trim()) {
            currentY += 3; // Small space for empty lines
            return;
          }
          
          if (isHeading(line)) {
            // Bold heading
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            const wrappedLines = doc.splitTextToSize(line, maxWidth);
            
            // Check if we need a new page for the heading and some content
            checkNewPage(wrappedLines.length * 6 + 10);
            
            wrappedLines.forEach(wrappedLine => {
              doc.text(wrappedLine, margin, currentY);
              currentY += 6;
            });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
          } else {
            // Normal text - wrap and check space for entire paragraph
            const wrappedLines = doc.splitTextToSize(line, maxWidth);
            const requiredSpace = wrappedLines.length * 5;
            
            // If paragraph won't fit, move to next page
            if (currentY + requiredSpace > pageHeight - margin) {
              doc.addPage();
              currentY = margin;
            }
            
            wrappedLines.forEach(wrappedLine => {
              doc.text(wrappedLine, margin, currentY);
              currentY += 5;
            });
          }
        });
        
        return currentY;
      };

      // Helper function to parse and render tables
      const parseTable = (text) => {
        const tableRegex = /\|(.+)\|\n\|[-:\s|]+\|\n((?:\|.+\|\n?)+)/g;
        const tables = [];
        let match;

        while ((match = tableRegex.exec(text)) !== null) {
          const headerRow = match[1].split('|').map(cell => cell.trim()).filter(cell => cell);
          const bodyRows = match[2].trim().split('\n').map(row => 
            row.split('|').map(cell => cell.trim()).filter(cell => cell)
          );
          
          tables.push({
            start: match.index,
            end: match.index + match[0].length,
            headers: headerRow,
            rows: bodyRows
          });
        }

        return tables;
      };

      // Helper function to render a table in PDF
      const renderTable = (table) => {
        const colWidth = maxWidth / table.headers.length;
        
        // Draw header
        checkNewPage(15);
        doc.setFillColor(255, 102, 0); // Orange
        doc.rect(margin, yPosition, maxWidth, 10, 'F');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255); // White text
        
        table.headers.forEach((header, i) => {
          doc.text(header, margin + (i * colWidth) + 2, yPosition + 7);
        });
        
        yPosition += 10;
        
        // Draw rows
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0); // Black text
        
        table.rows.forEach((row, rowIndex) => {
          checkNewPage(10);
          
          // Alternate row colors
          if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
            doc.rect(margin, yPosition, maxWidth, 8, 'F');
          }
          
          row.forEach((cell, i) => {
            const cellText = doc.splitTextToSize(cell, colWidth - 4);
            doc.text(cellText, margin + (i * colWidth) + 2, yPosition + 6);
          });
          
          yPosition += 8;
        });
        
        yPosition += 5; // Space after table
      };

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 102, 0);
      doc.text(chatData.title || 'Feedback Chat', margin, yPosition);
      yPosition += 8;

      // Metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Messages: ${chatData.messages.length}`, margin, yPosition);
      yPosition += 8;

      // Separator
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      // Process messages
      chatData.messages.forEach((msg, idx) => {
        const sender = msg.sender === 'user' ? 'You' : 'Feedback Agent';
        const senderColor = msg.sender === 'user' ? [0, 102, 204] : [255, 102, 0];

        checkNewPage(6);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...senderColor);
        doc.text(sender, margin, yPosition);
        yPosition += 6;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        
        const messageText = msg.text || '';
        
        // Parse tables from message
        const tables = parseTable(messageText);
        
        if (tables.length > 0) {
          // Process text with tables
          let lastIndex = 0;
          
          tables.forEach(table => {
            // Add text before table
            const textBefore = messageText.substring(lastIndex, table.start);
            if (textBefore.trim()) {
              const cleanedText = cleanMarkdown(textBefore);
              yPosition = renderTextWithHeadings(cleanedText, yPosition);
              yPosition += 3;
            }
            
            // Render table
            renderTable(table);
            
            lastIndex = table.end;
          });
          
          // Add text after last table
          const textAfter = messageText.substring(lastIndex);
          if (textAfter.trim()) {
            const cleanedText = cleanMarkdown(textAfter);
            yPosition = renderTextWithHeadings(cleanedText, yPosition);
          }
        } else {
          // No tables, just clean and render text
          const cleanedText = cleanMarkdown(messageText);
          yPosition = renderTextWithHeadings(cleanedText, yPosition);
        }

        yPosition += 2;

        // Separator between messages
        if (idx < chatData.messages.length - 1) {
          checkNewPage(8);
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
        }
      });

      const fileName = `${(chatData.title || 'feedback_chat').replace(/[^a-z0-9]/gi, '_')}_chat.pdf`;
      doc.save(fileName);
      
      console.log('✅ PDF exported successfully');
    } catch (error) {
      console.error('❌ Failed to export chat:', error);
      console.error('Error stack:', error.stack);
      alert(`Failed to export chat: ${error.message}`);
    }
  };

  const handleLoadChat = async (sessionId) => {
    try {
      const chatData = await loadChatSession(sessionId);
      if (chatData && chatData.messages && Array.isArray(chatData.messages)) {
        setMessages(chatData.messages);
        setCurrentSessionId(sessionId);
        // If loading a chat with messages, mark as started
        if (chatData.messages.length > 0) {
          setHasStartedChat(true);
        }
      }
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputValue("");
    setJobDescription("");
    setTranscript(null);
    setTranscriptFileName("");
    setCurrentSessionId(`session-${Date.now()}`);
    setSetupStep(1);
    setSetupCollapsed(false);
    setHasSubmitted(false);
    setInputBarCollapsed(false);
    setShowSetupOptions(false);
    setHasStartedChat(false); // Reset chat started state
  };

  const handleReuploadContext = () => {
    setSetupCollapsed(false);
    setSetupStep(1);
    setShowSetupOptions(false);
    // Don't clear existing data yet - let user decide
  };

  const handleBackToFollowUp = () => {
    setSetupCollapsed(true);
    // Don't hide options - let them stay visible
  };

  return (
    <div className="flex w-full h-full overflow-hidden">
      <AgentSidebar
        currentAgent="feedback"
        agentDisplayName="Feedback Agent"
        onNavigate={onNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onNewChat={handleNewChat}
        chatHistory={chatSessions}
        onLoadChat={handleLoadChat}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
        onShareChat={handleShareChat}
      />

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden transition-all duration-500 ease-in-out">
        {/* Theme Toggle - Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => toggleTheme(e)}
            className="theme-toggle-glow p-2 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:scale-110 active:scale-95 hover:rotate-12"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            <div className="relative">
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500 transition-all duration-500 rotate-0" key="sun" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 transition-all duration-500 rotate-0" key="moon" />
              )}
            </div>
          </button>
        </div>

        {/* Wrapper for centering when empty */}
        <div className={`flex-1 flex flex-col ${!hasStartedChat ? 'justify-center' : ''} overflow-hidden transition-all duration-500 ease-in-out`}>
          {/* CHAT AREA */}
          <div className={`${!hasStartedChat ? '' : 'flex-1 overflow-y-auto'} p-3 sm:p-4 md:p-6 transition-all duration-500`}>
            {!hasStartedChat ? (
              // BEAUTIFUL EMPTY STATE - Centered content
              <div className="text-center max-w-xl mx-auto opacity-90 px-4">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <img
                    src="/CI-logo.png"
                    alt="CI Logo"
                    className="h-12 w-auto sm:h-14 md:h-16"
                  />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white">
                    Feedback Agent
                  </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg mb-8">
                  Upload JD & transcript to generate evaluation
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto w-full space-y-4 sm:space-y-6">

                {messages.map((msg) => {
                  const isUser = msg.sender === "user";

                  return (
                    <div
                      key={msg.id}
                      className={`flex items-start gap-2 sm:gap-4 ${isUser ? "justify-end" : "justify-start"}`}
                    >
                      {/* Avatar */}
                      {!isUser && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-orange-600 flex items-center justify-center shadow-md flex-shrink-0">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                        </div>
                      )}

                      {/* MESSAGE CONTENT */}
                      <div
                        className={`text-sm sm:text-[15px] leading-relaxed font-['system-ui',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      ${isUser
                            ? "inline-block bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-3xl max-w-[85%] sm:max-w-xl md:max-w-2xl break-words whitespace-pre-wrap"
                            : "max-w-[85%] sm:max-w-xl md:max-w-2xl text-gray-800 dark:text-gray-200"
                          }`}
                      >
                        {msg.isThinking ? (
                          <div className="flex items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                            <span className="text-gray-700 dark:text-gray-300 font-medium">Analyzing feedback...</span>
                          </div>
                        ) : (
                          <>
                            <div className={`prose prose-gray dark:prose-invert max-w-none ${isUser ? 'text-gray-900 dark:text-gray-100' : 'text-gray-800 dark:text-gray-200'}`}>
                              <ReactMarkdown
  remarkPlugins={[remarkGfm]}
  components={{
    h1: ({ ...props }) => (
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-6 mb-3 border-b pb-2 border-gray-200 dark:border-gray-700" {...props} />
    ),
    h2: ({ ...props }) => (
      <h2 className="text-xl font-bold text-orange-600 dark:text-orange-400 mt-6 mb-3" {...props} />
    ),
    h3: ({ ...props }) => (
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-5 mb-2" {...props} />
    ),
    h4: ({ ...props }) => (
      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2" {...props} />
    ),

    p: ({ ...props }) => (
      <p className="mb-4 leading-relaxed text-gray-800 dark:text-gray-200" {...props} />
    ),

    ul: ({ ...props }) => (
      <ul className="list-disc list-inside mb-4 space-y-2 ml-2 text-gray-800 dark:text-gray-200" {...props} />
    ),

    ol: ({ ...props }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 ml-2 text-gray-800 dark:text-gray-200" {...props} />
    ),

    li: ({ ...props }) => (
      <li className="leading-relaxed" {...props} />
    ),

    strong: ({ ...props }) => (
      <strong className="font-semibold text-gray-900 dark:text-white" {...props} />
    ),

    em: ({ ...props }) => (
      <em className="italic text-orange-600 dark:text-orange-400" {...props} />
    ),

    blockquote: ({ ...props }) => (
      <blockquote className="border-l-4 border-orange-500 pl-4 italic text-gray-600 dark:text-gray-400 my-4" {...props} />
    ),

    code: ({ inline, ...props }) =>
      inline ? (
        <code className="bg-gray-100 dark:bg-gray-800 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
      ) : (
        <code className="block bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto my-4" {...props} />
      ),

    pre: ({ ...props }) => (
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto my-4" {...props} />
    ),

    hr: ({ ...props }) => (
      <hr className="my-6 border-gray-300 dark:border-gray-700" {...props} />
    ),

    /* --------- BEAUTIFUL STRUCTURED TABLES --------- */

    table: ({ ...props }) => (
      <div className="overflow-x-auto my-6 mb-8 rounded-2xl border-2 border-orange-400 dark:border-orange-600 shadow-2xl bg-white dark:bg-gray-800">
        <table className="min-w-full border-collapse" {...props} />
      </div>
    ),

    thead: ({ ...props }) => (
      <thead className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600" {...props} />
    ),

    th: ({ ...props }) => (
      <th className="px-6 py-4 text-sm font-extrabold text-white uppercase tracking-wider text-center border-r-2 border-orange-400 last:border-r-0 whitespace-nowrap" {...props} />
    ),

    td: ({ ...props }) => (
      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 font-medium text-left border-r border-gray-300 dark:border-gray-600 last:border-r-0 align-top" {...props} />
    ),

    tr: ({ ...props }) => (
      <tr className="hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-150 border-b border-gray-300 dark:border-gray-700 last:border-b-0" {...props} />
    ),

    tbody: ({ ...props }) => (
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-300 dark:divide-gray-700" {...props} />
    ),
  }}
>
  {msg.text}
</ReactMarkdown>
                            </div>

                            {msg.metrics && (
                              <div className="mt-4 pt-3 border-t-2 border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-gray-400 dark:text-gray-400 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Input: {msg.metrics.inputTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Output: {msg.metrics.outputTokens?.toLocaleString() || 0} tokens</span>
                                  </div>
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Total: {msg.metrics.totalTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Elapsed Time: {msg.metrics.executionTimeMs ? `${msg.metrics.executionTimeMs}ms` : 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* FLOATING INPUT BAR - Centered when empty, bottom when chat active */}
        <div className={`w-full flex flex-col items-center px-2 sm:px-0 transition-all duration-500 ease-in-out ${!hasStartedChat ? 'pb-[25vh]' : 'py-3 sm:py-4 md:py-6'}`}>
          <div className="w-full sm:w-[90%] max-w-4xl">
            {/* Prompt limit warning */}
            {isPromptLimitReached && (
              <div className="mb-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg px-4 py-2 text-center">
                <p className="text-sm text-orange-800 dark:text-orange-300">
                  <span className="font-semibold">Prompt limit reached!</span> You've used {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts.
                  <button
                    onClick={handleNewChat}
                    className="ml-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline font-semibold"
                  >
                    Start a new chat
                  </button>
                </p>
              </div>
            )}

            {/* Prompt counter */}
            {!isPromptLimitReached && userPromptCount > 0 && (
              <div className="mb-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts used
                </p>
              </div>
            )}

            {/* Constant Toggle Button - Always visible when chat has started */}
            {hasStartedChat && (
              <div className="mb-2 flex justify-end">
                <button
                  onClick={() => setInputBarCollapsed(!inputBarCollapsed)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-full transition-all hover:shadow-md"
                  title={inputBarCollapsed ? "Show input bar" : "Hide input bar"}
                >
                  {inputBarCollapsed ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" />
                      <span>Show Input</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      <span>Hide Input</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Input Bar Content */}
            {!inputBarCollapsed && (
              <>
                {/* MULTI-STEP SETUP FLOW */}
                {!setupCollapsed ? (
              <div className="space-y-3">
                {/* Back to Follow-up button - Show when user has already submitted and is re-uploading */}
                {hasSubmitted && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-2xl px-4 py-2.5 flex items-center justify-between shadow-sm">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                      Re-uploading new context. Your previous context will be replaced.
                    </p>
                    <button
                      onClick={handleBackToFollowUp}
                      className="text-sm font-semibold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline whitespace-nowrap ml-3"
                    >
                      ← Back to Follow-up
                    </button>
                  </div>
                )}
                {/* Step 1: Job Description */}
                {setupStep >= 1 && (
                  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-3 shadow-lg transition-all">
                    {setupStep === 1 ? (
                      <>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Step 1: Job Description
                        </label>
                        <textarea
                          value={jobDescription}
                          onChange={(e) => setJobDescription(e.target.value)}
                          placeholder="Paste the job description here..."
                          disabled={isPromptLimitReached}
                          rows={3}
                          className={`w-full p-2 bg-transparent border border-gray-300 dark:border-gray-600 rounded-lg outline-none text-gray-800 dark:text-gray-200 text-sm resize-none focus:ring-2 focus:ring-orange-500 ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={() => {
                              if (jobDescription.trim()) {
                                setSetupStep(2);
                              } else {
                                alert('Please enter a job description');
                              }
                            }}
                            disabled={!jobDescription.trim() || isPromptLimitReached}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              jobDescription.trim() && !isPromptLimitReached
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Job Description</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{jobDescription.substring(0, 60)}...</p>
                        </div>
                        <button
                          onClick={() => setSetupStep(1)}
                          className="text-orange-600 dark:text-orange-400 hover:text-orange-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Transcript Upload */}
                {setupStep >= 2 && (
                  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-3 shadow-lg transition-all">
                    {setupStep === 2 ? (
                      <>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Step 2: Upload Transcript
                        </label>
                        {!transcript ? (
                          <div
                            onClick={() => !isPromptLimitReached && fileInputRef.current?.click()}
                            className={`border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-orange-500 transition-colors ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">Click to upload transcript</p>
                            <p className="text-xs text-gray-500 mt-1">TXT, PDF, or DOCX</p>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">{transcriptFileName}</span>
                            </div>
                            <button
                              onClick={removeTranscript}
                              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            >
                              <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".txt,.pdf,.docx,.doc"
                          className="hidden"
                          onChange={handleFileUpload}
                          disabled={isPromptLimitReached}
                        />
                        <div className="flex justify-between mt-2">
                          <button
                            onClick={() => setSetupStep(1)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Back
                          </button>
                          <button
                            onClick={() => {
                              if (transcript) {
                                setSetupStep(3);
                              } else {
                                alert('Please upload a transcript');
                              }
                            }}
                            disabled={!transcript || isPromptLimitReached}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              transcript && !isPromptLimitReached
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Transcript</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{transcriptFileName}</p>
                        </div>
                        <button
                          onClick={() => setSetupStep(2)}
                          className="text-orange-600 dark:text-orange-400 hover:text-orange-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Optional Question & Review */}
                {setupStep >= 3 && (
                  <div className="space-y-3">
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-3 shadow-lg transition-all">
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Step 3: Optional Question
                      </label>
                      <input
                        ref={textareaRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Add an optional question (or leave blank)"
                        disabled={isPromptLimitReached}
                        className={`w-full p-2 bg-transparent border border-gray-300 dark:border-gray-600 rounded-lg outline-none text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      <div className="flex justify-between mt-2">
                        <button
                          onClick={() => setSetupStep(2)}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Back
                        </button>
                      </div>
                    </div>

                    {/* Review Card */}
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-2xl px-4 py-3 shadow-lg">
                      <h3 className="text-sm font-bold text-orange-900 dark:text-orange-300 mb-2">Review & Submit</h3>
                      <div className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        <p><span className="font-semibold">JD:</span> {jobDescription.substring(0, 50)}...</p>
                        <p><span className="font-semibold">Transcript:</span> {transcriptFileName}</p>
                        {inputValue && <p><span className="font-semibold">Question:</span> {inputValue}</p>}
                      </div>
                      <button
                        onClick={sendMessage}
                        disabled={isLoading || isPromptLimitReached}
                        className={`w-full mt-3 px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                          !isLoading && !isPromptLimitReached
                            ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Get Feedback
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* COLLAPSED SETUP - After submission */
              <div className="space-y-3">
                {/* Show options after first response */}
                {showSetupOptions ? (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-2xl px-4 py-3 shadow-lg">
                    <h3 className="text-sm font-bold text-orange-900 dark:text-orange-300 mb-2.5 text-center">What would you like to do next?</h3>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => setShowSetupOptions(false)}
                        className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border-2 border-orange-500 text-orange-600 dark:text-orange-400 rounded-lg font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                      >
                        Ask a Question
                      </button>
                      <button
                        onClick={handleReuploadContext}
                        className="flex-1 px-3 py-2 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors shadow-md"
                      >
                        Re-upload JD & Transcript
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-2 shadow-lg">
                      <button
                        onClick={() => setSetupCollapsed(false)}
                        className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                      >
                        <span>Feedback Context</span>
                        <span className="text-xs text-gray-500">(Click to expand)</span>
                      </button>
                    </div>

                    {/* Re-upload banner - Same style as Back to Follow-up */}
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-2xl px-4 py-2.5 flex items-center justify-between shadow-sm">
                      <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                        Want to provide new JD & Transcript for fresh feedback?
                      </p>
                      <button
                        onClick={handleReuploadContext}
                        className="text-sm font-semibold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline whitespace-nowrap ml-3"
                      >
                        Re-upload →
                      </button>
                    </div>

                    {/* Follow-up Question Input */}
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-3 shadow-lg">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && inputValue.trim() && !isLoading && !isPromptLimitReached) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          placeholder="Ask a follow-up question..."
                          disabled={isLoading || isPromptLimitReached}
                          className={`flex-1 p-2 bg-transparent border-none outline-none text-gray-800 dark:text-gray-200 text-sm placeholder-gray-400 dark:placeholder-gray-500 ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <button
                          onClick={sendMessage}
                          disabled={!inputValue.trim() || isLoading || isPromptLimitReached}
                          className={`p-2 rounded-full transition-all ${
                            inputValue.trim() && !isLoading && !isPromptLimitReached
                              ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md'
                              : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackAgent;